import React from "react";
import { useToonId } from "./toon";

/*
 * Kontorszombies som kliver upp ur kyrkogården, hasar uppför stigen till
 * änkehuset, svänger in genom grinden och försvinner in genom porten.
 * En kommer längs gatan och en stannar ute och vankar fram och tillbaka. Tecknade som de andra figurerna: mjuk
 * skuggning utan konturlinjer, glansiga (men trötta) ögon.
 *
 * Figuren ritas vänd åt höger med fötterna i (0, 0) och är ungefär 68
 * enheter hög; den skalas ner till porthöjd. Rutterna anges i samma
 * koordinater som Enkehus (trappans mitt = 0, 0).
 */

type Two = [string, string];

interface Look {
  shirt: Two;
  legs: Two;
  jacket?: Two;
  tie?: string;
  hair?: string;
  hairStyle: "messy" | "bob" | "bald";
  prop: "briefcase" | "coffee" | "lanyard";
  glasses?: boolean;
  skirt?: boolean;
  /** Hälta: bakre benet släpar. */
  limp?: boolean;
}

const SKIN: Two = ["#9db583", "#667f55"];
const SKIN_LIGHT = "#c6d9aa";
const SHOE = "#150e0b";

const LOOKS: Record<"suit" | "coffee" | "shirt" | "boss", Look> = {
  suit: { shirt: ["#e9e3d6", "#a89f90"], legs: ["#2b2d38", "#191a22"], jacket: ["#2d3448", "#1a1e2b"], tie: "#b3302a", hair: "#3a2a1e", hairStyle: "messy", prop: "briefcase" },
  coffee: { shirt: ["#c98a9a", "#8f5a68"], legs: ["#3a3040", "#221c27"], hair: "#6b3f22", hairStyle: "bob", prop: "coffee", skirt: true },
  shirt: { shirt: ["#b8cbd9", "#7c8fa0"], legs: ["#3b3a36", "#23221f"], tie: "#2f5d8c", hair: "#1e1714", hairStyle: "messy", prop: "lanyard", limp: true },
  boss: { shirt: ["#ece8dc", "#aaa294"], legs: ["#3d3328", "#241d16"], tie: "#5c7a3a", hairStyle: "bald", prop: "lanyard", glasses: true },
};

const OfficeZombie: React.FC<{ look: Look; step: number }> = ({ look, step }) => {
  const id = useToonId("zb");
  const { shirt, legs, jacket } = look;
  const amp = 15;
  const dur = `${step}s`;
  const half = `${step / 2}s`;

  const leg = (front: boolean) => {
    const a = front ? amp : look.limp ? -5 : -amp;
    const c = front ? legs[0] : legs[1];
    return (
      <g>
        <animateTransform attributeName="transform" type="rotate" values={`${-a} 0 -31; ${a} 0 -31; ${-a} 0 -31`} dur={dur} repeatCount="indefinite" />
        <path d="M-3.6 -33 L-3.1 -4 H3.1 L3.6 -33 Z" fill={look.skirt ? (front ? "#2a2230" : "#1a151e") : c} />
        <path d="M-3.8 -4.6 H3.8 C 7.6 -4.6, 9.4 -2.4, 9.4 0 H-3.8 Z" fill={SHOE} />
        <path d="M0 -4 C 3 -4.2, 6 -3.8, 8 -2.2" stroke="#3a2a22" strokeWidth="0.8" fill="none" />
      </g>
    );
  };

  /** Utsträckt arm (axel i 3, -50) med trasig manschett och slappa fingrar. */
  const reachArm = (back: boolean) => {
    const sleeve = jacket ?? shirt;
    const c = back ? sleeve[1] : sleeve[0];
    const skin = back ? SKIN[1] : SKIN[0];
    return (
      <g transform={back ? "translate(-2.4 -1.6)" : undefined}>
        <animateTransform
          attributeName="transform"
          type="rotate"
          values={back ? "4 3 -50; -3 3 -50; 4 3 -50" : "-4 3 -50; 3 3 -50; -4 3 -50"}
          additive="sum"
          dur={dur}
          repeatCount="indefinite"
        />
        <path d="M0 -54 C 8 -53.4, 16 -52.4, 22 -51.8 L 22.4 -46.6 C 16 -47, 8 -46.8, 1 -45.8 Z" fill={c} />
        {!back && <path d="M3 -53.2 C 10 -52.6, 16 -52, 21 -51.6" stroke={sleeve[0] === c ? "#ffffff" : c} strokeWidth="0.9" opacity="0.25" fill="none" />}
        <path d="M19.6 -52 L22.6 -50.4 L21 -49 L22.8 -46.6 L19.6 -47 Z" fill={back ? sleeve[1] : sleeve[1]} />
        <ellipse cx="25" cy="-49" rx="3.6" ry="2.8" fill={skin} />
        <path d="M26.2 -47 l1.4 3 M24.2 -46.6 l0.4 3.2 M28 -48.2 l1.8 2.4" stroke={skin} strokeWidth="1.4" strokeLinecap="round" />
      </g>
    );
  };

  const frontArm = () => {
    const sleeve = jacket ?? shirt;
    if (look.prop === "briefcase") {
      return (
        <g>
          <animateTransform attributeName="transform" type="rotate" values="-8 3 -50; 8 3 -50; -8 3 -50" dur={dur} repeatCount="indefinite" />
          <path d="M0.6 -53 C 3 -46, 4.4 -40, 5 -34.6 L 9.4 -35.2 C 8.6 -41, 7 -47, 5.6 -52.6 Z" fill={sleeve[0]} />
          <ellipse cx="7.2" cy="-33" rx="2.8" ry="2.6" fill={SKIN[0]} />
          <path d="M4.6 -33 C 4.6 -36, 9.8 -36, 9.8 -33" stroke="#2a1a10" strokeWidth="1.1" fill="none" />
          <rect x="0.6" y="-32.4" width="13.4" height="10" rx="1.4" fill="#4d3322" />
          <rect x="0.6" y="-32.4" width="13.4" height="2.6" rx="1.2" fill="#6b4a32" />
          <rect x="6.4" y="-29.4" width="2" height="1.6" fill="#c9a24a" />
        </g>
      );
    }
    if (look.prop === "coffee") {
      return (
        <g>
          <path d="M0.6 -53 C 3 -48, 5 -44, 8.4 -41.6 L 11.4 -44.6 C 9 -46.4, 7 -49, 5.8 -52.6 Z" fill={sleeve[0]} />
          <path d="M8.4 -41.6 C 11 -43, 14 -44.6, 17 -45.4 L 16.6 -49.2 C 13.4 -48.6, 10.6 -47, 8 -45.4 Z" fill={sleeve[0]} />
          {/* Fikamuggen – den enda som fortfarande är varm */}
          <path d="M16.4 -52.6 H23.4 L22.8 -44.2 H17 Z" fill="#f1ece2" />
          <rect x="16.6" y="-50" width="6.6" height="1.8" fill="#f28a3c" />
          <path d="M23.2 -51 C 26 -51, 26 -46.6, 22.9 -46.6" stroke="#f1ece2" strokeWidth="1.3" fill="none" />
          <ellipse cx="19.4" cy="-47.6" rx="3" ry="2.6" fill={SKIN[0]} />
          <g stroke="#ffffff" strokeWidth="0.9" fill="none" strokeLinecap="round" opacity="0.5">
            <path d="M18.4 -54 C 17 -56.6, 20 -57.8, 18.6 -60.6">
              <animate attributeName="opacity" values="0;0.9;0" dur="2.4s" repeatCount="indefinite" />
            </path>
            <path d="M21.2 -54.4 C 20 -57, 22.8 -58.4, 21.4 -61">
              <animate attributeName="opacity" values="0.9;0;0.9" dur="2.4s" repeatCount="indefinite" />
            </path>
          </g>
        </g>
      );
    }
    return reachArm(false);
  };

  return (
    <g>
      <ellipse cx="3" cy="0" rx="15" ry="2.8" fill="#000000" opacity="0.4" />
      {leg(false)}
      {leg(true)}
      <g>
        <animateTransform attributeName="transform" type="translate" values="0 0; 0 -1.4; 0 0" dur={half} repeatCount="indefinite" />
        {reachArm(true)}
        {/* Kjol eller byxlinning */}
        {look.skirt ? (
          <path d="M-7.6 -33 H9 L10.6 -20 C 4 -18.6, -2 -18.6, -8.4 -20 Z" fill={legs[0]} />
        ) : (
          <rect x="-7" y="-33" width="15" height="2.6" fill={legs[1]} />
        )}
        {/* Kroppen, framåtlutad */}
        <path d="M-7 -31 C -9 -42, -7 -53, 0 -56 C 6 -58, 10 -54, 10 -46 C 10 -40, 9 -35, 8 -31 Z" fill={shirt[0]} />
        <path d="M-7 -31 C -9 -42, -7 -53, 0 -56 C -3 -50, -3.6 -40, -2 -31 Z" fill={shirt[1]} />
        <path d="M-7 -31.4 L8 -31.4 L7.2 -28.2 L4.6 -30.2 L2 -27.6 L-1 -30.2 L-4 -28.2 L-7 -30 Z" fill={shirt[0]} />
        <path d="M4.6 -54.4 C 7.6 -54, 9 -51, 9 -47" stroke="#ffffff" strokeWidth="1.2" fill="none" opacity="0.35" />
        {jacket && (
          <>
            <path d="M-7.6 -30 C -9.6 -42, -7.6 -54, 0 -57 C 3 -58, 5 -57.6, 6 -56.6 L 4 -44 L 5.4 -29.4 Z" fill={jacket[0]} />
            <path d="M-7.6 -30 C -9.6 -42, -7.6 -54, 0 -57 C -3.4 -50, -3.4 -40, -2.4 -29.6 Z" fill={jacket[1]} />
            <path d="M6 -56.6 L3.6 -49 L6.4 -46.8 Z" fill={jacket[1]} />
            <path d="M-4 -40 L -2 -38 L -4.6 -36.4 Z" fill={shirt[1]} />
          </>
        )}
        {look.tie && (
          <path d="M7.8 -55.4 L9.8 -54.4 L10.4 -41 L8.9 -38.2 L7.8 -41 Z" fill={look.tie}>
            <animateTransform attributeName="transform" type="rotate" values="-6 8.8 -55; 7 8.8 -55; -6 8.8 -55" dur={dur} repeatCount="indefinite" />
          </path>
        )}
        {look.prop === "lanyard" && (
          <g>
            <path d="M4 -56.6 C 5 -52, 7.6 -48, 9.6 -45.4" stroke="#2f6fb0" strokeWidth="0.8" fill="none" />
            <rect x="7.8" y="-45.6" width="4" height="5" rx="0.5" fill="#f4f1ea" />
            <rect x="7.8" y="-45.6" width="4" height="1.4" rx="0.5" fill="#2f6fb0" />
            <rect x="8.6" y="-43.4" width="1.6" height="1.6" fill="#8a9aa8" />
          </g>
        )}
        {/* Nacke och huvud, som vinglar */}
        <path d="M2 -58.6 L6.4 -58.6 L6.8 -54 L2 -54 Z" fill={SKIN[1]} />
        <g>
          <animateTransform attributeName="transform" type="rotate" values="-7 4 -57; 5 4 -57; -7 4 -57" dur={`${step * 1.6}s`} repeatCount="indefinite" />
          <defs>
            <clipPath id={`${id}-h`}>
              <ellipse cx="7.6" cy="-63" rx="6.9" ry="7.5" transform="rotate(18 7.6 -63)" />
            </clipPath>
          </defs>
          <ellipse cx="7.6" cy="-63" rx="6.9" ry="7.5" transform="rotate(18 7.6 -63)" fill={SKIN[1]} />
          <g clipPath={`url(#${id}-h)`}>
            <ellipse cx="9.4" cy="-64" rx="6.9" ry="7.5" transform="rotate(18 9.4 -64)" fill={SKIN[0]} />
            <ellipse cx="10" cy="-67.6" rx="2.6" ry="1.4" transform="rotate(-20 10 -67.6)" fill={SKIN_LIGHT} opacity="0.7" />
            {/* Ett lagat sår i bakhuvudet */}
            <path d="M2.4 -60 L5.6 -66.4" stroke="#4a5c3e" strokeWidth="0.8" />
            <path d="M3 -61.8 l1.6 0.8 M3.9 -63.6 l1.6 0.8 M4.8 -65.4 l1.6 0.8" stroke="#4a5c3e" strokeWidth="0.6" />
          </g>
          <ellipse cx="4.6" cy="-62.6" rx="1.6" ry="2.3" fill={SKIN[1]} />
          <path d="M14.2 -63.4 C 16.2 -62.6, 16 -61, 14.4 -60.8 Z" fill={SKIN[1]} />
          {/* Hängande underkäke med en ensam tand */}
          <path d="M11 -58.6 Q 13.8 -59.2 14.4 -57.2 Q 13.4 -54.4 10.8 -55.4 Z" fill="#2a1010" />
          <rect x="12.4" y="-58.9" width="1.1" height="1.3" fill="#efe8cf" />
          {/* Trött, glansigt öga */}
          <ellipse cx="11.6" cy="-64.4" rx="2.3" ry="2.1" fill="#f3f0d6" />
          <circle cx="12.4" cy="-64.1" r="0.8" fill="#3a0b0b" />
          <circle cx="12.7" cy="-64.5" r="0.3" fill="#ffffff" />
          <path d="M9.2 -65.6 Q 11.6 -67.4 14 -65.8 L 14 -65 Q 11.6 -66 9.2 -64.8 Z" fill={SKIN[1]} />
          <path d="M9.6 -62.2 Q 11.6 -61.2 13.6 -62.4" stroke="#5e5174" strokeWidth="0.7" fill="none" opacity="0.8" />
          {look.glasses && (
            <g stroke="#15110f" strokeWidth="0.7" fill="none">
              <circle cx="11.8" cy="-64.2" r="2.9" fill="#ffffff" fillOpacity="0.12" />
              <path d="M8.9 -64.4 L 5 -63.4 M 10.6 -66.4 L 12.4 -63.8" />
            </g>
          )}
          {look.hairStyle === "messy" && (
            <path
              d="M1.4 -63.6 C 0.8 -70, 6 -72.6, 11 -70.6 L 12.6 -72.2 L 12.2 -69.6 C 13.8 -68.8 14.2 -67.6 14 -66.8 C 11 -68.8, 7.2 -68.6, 4.6 -66.2 L 3.8 -64 L 2.8 -65.4 Z"
              fill={look.hair}
            />
          )}
          {look.hairStyle === "bob" && (
            <path d="M0.8 -59.6 C -0.8 -68, 5 -73, 11 -71.2 C 14 -70.2, 15.2 -67.8, 14.8 -66.2 C 11 -68, 8 -67.2, 6.6 -64.6 C 6 -61.2, 5.6 -58.2, 4 -56.6 C 2.4 -56.4, 1.3 -58, 0.8 -59.6 Z" fill={look.hair} />
          )}
          {look.hairStyle === "bald" && <path d="M3 -67.6 C 6 -71, 10 -71.2, 13 -69 M3.4 -66 C 6 -69.6, 10 -70, 12.6 -68.2" stroke="#3a2c22" strokeWidth="0.6" fill="none" />}
        </g>
        {frontArm()}
      </g>
    </g>
  );
};

interface Route {
  look: Look;
  path: string;
  dur: number;
  begin: number;
  step: number;
  /** Tid (0..1) då den svänger in mot porten och då den är inne. */
  turn?: number;
  end?: number;
  facing: "left" | "right" | "pace";
  fadeIn?: number;
}

/** Trottoaren framför staketet och tröskeln i porten. */
const STREET = 19;
const DOORSTEP = -6;
const SPEED = 10.5;

type Pt = [number, number];

/**
 * Rutt som slutar i porten. `points` är vägen fram till grinden (x = 0 på
 * trottoaren); sista biten upp till tröskeln läggs till här.
 */
const entering = (look: Look, points: Pt[], dur: number, begin: number, step: number, fadeIn = 1): Route => {
  const pts: Pt[] = [...points, [0, STREET], [0, DOORSTEP]];
  const segs = pts.slice(1).map((q, i) => Math.hypot(q[0] - pts[i][0], q[1] - pts[i][1]));
  const len = segs.reduce((a, b) => a + b, 0);
  const outside = len - segs[segs.length - 1];
  const end = len / SPEED / dur;
  return {
    look,
    path: `M${pts.map(q => q.join(" ")).join(" L")}`,
    dur,
    begin,
    step,
    turn: (end * outside) / len,
    end,
    facing: points[0][0] > 0 ? "left" : "right",
    fadeIn: fadeIn / dur,
  };
};

/** Stigen från kyrkogården upp till grinden (startar dold bakom kullen i förgrunden). */
export const HILL_TRAIL: Pt[] = [
  [-341, 115],
  [-190, 48],
  [-94, 25],
  [-23, STREET],
];

const ROUTES: Route[] = [
  entering(LOOKS.suit, HILL_TRAIL, 46, -6, 1.3, 0.1),
  entering(LOOKS.coffee, HILL_TRAIL, 46, -29, 1.2, 0.1),
  entering(LOOKS.boss, [[260, STREET]], 36, -16, 1.45),
  { look: LOOKS.shirt, path: `M40 ${STREET + 2} H150 H40`, dur: 26, begin: -6, step: 1.5, facing: "pace" },
];

const f = (n: number) => n.toFixed(4);

const Walker: React.FC<{ r: Route }> = ({ r }) => {
  const timing = { dur: `${r.dur}s`, begin: `${r.begin}s`, repeatCount: "indefinite" };
  const inside = r.end !== undefined && r.turn !== undefined;
  const flip =
    r.facing === "left" ? (
      <g transform="scale(-1 1)">
        <OfficeZombie look={r.look} step={r.step} />
      </g>
    ) : r.facing === "pace" ? (
      <g>
        <animateTransform attributeName="transform" type="scale" values="1 1;-1 1" keyTimes="0;0.5" calcMode="discrete" {...timing} />
        <OfficeZombie look={r.look} step={r.step} />
      </g>
    ) : (
      <OfficeZombie look={r.look} step={r.step} />
    );
  return (
    <g opacity={inside ? 0 : 1}>
      {inside ? (
        <>
          <animateMotion path={r.path} calcMode="linear" keyPoints="0;1;1" keyTimes={`0;${f(r.end!)};1`} {...timing} />
          <animate
            attributeName="opacity"
            values="0;1;1;0;0"
            keyTimes={`0;${f(r.fadeIn!)};${f(r.end! - 1.4 / r.dur)};${f(r.end!)};1`}
            {...timing}
          />
        </>
      ) : (
        <animateMotion path={r.path} {...timing} />
      )}
      <g>
        {inside && (
          <animateTransform attributeName="transform" type="scale" values="1;1;0.8;0.8" keyTimes={`0;${f(r.turn!)};${f(r.end!)};1`} {...timing} />
        )}
        <g transform="scale(0.55)">{flip}</g>
      </g>
    </g>
  );
};

/** Zombierna i Enkehus koordinater: samma x, y och s som huset. */
const Zombies: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    {ROUTES.map((r, i) => (
      <Walker key={i} r={r} />
    ))}
  </g>
);

export default Zombies;
